import React, { useState } from 'react';
import PropTypes from 'prop-types';
import ReactEcharts from 'echarts-for-react';
import merge from 'lodash.merge';

import { traitsPropType, stylePropType } from 'src/PropTypes';
import { Style1 } from 'src/styles';

import { Div, Tag, Text } from '@pinpoint/design-kit';

import themes from 'src/themes';

function Heatmap({ title, labels, series, strength, style, options, formatter, onMouseover, theme }) {
	const xAxis = series.map(({ name }) => name);
	const yAxis = labels
		.map(({ trait, factor }) => ({
			value: trait,
			textStyle: {
				padding: [2, 6, 2, 6],
				borderRadius: 2,
				color: Style1.COLORS.DEMOGRAPHIC.PRIMARY,
				borderWidth: 1,
				borderColor: Style1.COLORS[factor.toUpperCase()].PRIMARY,
			},
		}))
		.reverse();
	const data = [];
	const allValues = [];
	for (let i = 0; i < xAxis.length; i += 1) {
		for (let j = 0; j < yAxis.length; j += 1) {
			data.push([i, yAxis.length - 1 - j, series[i].values[j]]);
			allValues.push(series[i].values[j]);
		}
	}
	const strengthData = [];
	const maxStrength = Math.max(...strength);
	const strengthPercentile = strength.map((s) => (s * 100) / maxStrength);
	for (let i = 0; i < strength.length; i += 1) {
		strengthData.push([0, strength.length - 1 - i, strengthPercentile[i]]);
	}
	const chartOption = {
		title: {
			text: title,
			...style.global.title,
		},
		toolbox: {
			...style.global.toolbox,
		},
		textStyle: {
			...style.Heatmap.textStyle,
		},
		label: {
			...style.Heatmap.label,
		},
		backgroundColor: style.global.backgroundColor,
		tooltip: {
			show: true,
			confine: true,
			formatter,
		},
		grid: [
			{
				...style.Heatmap.grid,
				left: '15%',
				width: series.length === 2 ? '15%' : '5%',
				top: 30,
				bottom: 20,
			},
			{
				...style.Heatmap.grid,
				left: series.length === 2 ? '60%' : '45%',
				width: series.length === 2 ? '40%' : '55%',
				top: 30,
				bottom: 20,
			},
		],
		dataZoom: [
			{
				type: 'slider',
				show: false,
				yAxisIndex: 1,
				orient: 'vertical',
			},
		],
		xAxis: [
			{
				type: 'category',
				data: [series.length === 1 ? 'Strength' : 'Divergence'],
				...style.Heatmap.xAxis,
				gridIndex: 0,
			},
			{
				type: 'category',
				data: xAxis,
				...style.Heatmap.xAxis,
				gridIndex: 1,
				axisLabel: {
					color: (value, index) => themes[series[index].theme].color[0],
					padding: [2, 4, 2, 4],
				},
			},
		],
		yAxis: [
			{
				type: 'category',
				data: yAxis,
				...style.Heatmap.yAxis,
				show: false,
				gridIndex: 0,
			},
			{
				type: 'category',
				data: yAxis,
				...style.Heatmap.yAxis,
				gridIndex: 1,
			},
		],
		visualMap: [
			{
				min: Math.floor(strengthPercentile[strength.length - 1]),
				max: Math.ceil(strengthPercentile[0]),
				calculable: true,
				show: true,
				seriesIndex: 0,
				orient: 'vertical',
				left: '0',
				top: 'middle',
				bottom: 'middle',
				itemHeight: '600',
				...style.Heatmap.visualMap.strength,
			},
			{
				min: Math.min(...allValues),
				max: Math.max(...allValues),
				show: false,
				seriesIndex: 1,
				...style.Heatmap.visualMap.series,
			},
		],
		series: [
			{
				type: 'heatmap',
				data: strengthData,
				xAxisIndex: 0,
				yAxisIndex: 0,
			},
			{
				type: 'heatmap',
				name: 'px',
				data,
				xAxisIndex: 1,
				yAxisIndex: 1,
			},
		],
	};
	const mergedOptions = merge(chartOption, options);
	const [hoveredFactor, setHoverFactor] = useState(null);
	return (
		<>
			<Div w="100%" h="calc(100% - 2.5rem)">
				<ReactEcharts
					ref={(element) => {
						const instance = element ? element.getEchartsInstance() : null;
						if (instance) {
							instance.off('mouseover');
							instance.on('mouseover', 'series', ({ data: datum }) => {
								const [, _index] = datum;
								const index = labels.length - 1 - _index;
								const label = labels[index];
								const values = series.map((_series) => _series.values[index]);
								onMouseover({ label, values });
								setHoverFactor(label.factor.toUpperCase());
							});
							instance.off('datarangeselected');
							instance.on('datarangeselected', ({ selected }) => {
								const [strengthStart, strengthEnd] = selected;
								let startValue = yAxis[0].value;
								let endValue = 0;
								for (let i = 0; i < strength.length; i += 1) {
									if (strengthPercentile[i] < strengthStart) {
										startValue = yAxis[yAxis.length - 1 - i].value;
										break;
									}
									if (!endValue && strengthPercentile[i] <= strengthEnd) {
										endValue = yAxis[yAxis.length - 1 - i].value;
									}
								}
								if (!endValue) endValue = startValue;
								instance.dispatchAction({
									type: 'dataZoom',
									startValue,
									endValue,
								});
							});
						}
					}}
					style={{
						width: '100%',
						height: '100%',
					}}
					option={mergedOptions}
					theme={theme}
				/>
			</Div>
			<Div
				d="flex"
				justify="space-between"
				m={{ x: { lg: '2rem', m: '1rem', xs: '0.25rem' } }}
				p={{ y: '0.2rem' }}
				h="1.5rem"
			>
				<Tag
					style={{
						border: '2px solid',
						borderColor: style.COLORS.OPENNESS.PRIMARY,
						color: style.COLORS.DEMOGRAPHIC.PRIMARY,
						boxShadow: hoveredFactor === 'OPENNESS' ? '0px 0px 3px 3px rgba(0,0,0,0.25)' : 'none',
					}}
				>
					<Text textSize="22">OPENNESS</Text>
				</Tag>
				<Tag
					style={{
						border: '2px solid',
						padding: '6px 8px',
						borderColor: style.COLORS.CONSCIENTIOUSNESS.PRIMARY,
						color: style.COLORS.DEMOGRAPHIC.PRIMARY,
						boxShadow: hoveredFactor === 'CONSCIENTIOUSNESS' ? '0px 0px 3px 3px rgba(0,0,0,0.25)' : 'none',
					}}
				>
					<Text textSize="22">CONSCIENTIOUSNESS</Text>
				</Tag>
				<Tag
					style={{
						border: '2px solid',
						padding: '6px 8px',
						borderColor: style.COLORS.EXTRAVERSION.PRIMARY,
						color: style.COLORS.DEMOGRAPHIC.PRIMARY,
						boxShadow: hoveredFactor === 'EXTRAVERSION' ? '0px 0px 3px 3px rgba(0,0,0,0.25)' : 'none',
					}}
				>
					<Text>EXTRAVERSION</Text>
				</Tag>
				<Tag
					style={{
						border: '2px solid',
						padding: '6px 8px',
						borderColor: style.COLORS.AGREEABLENESS.PRIMARY,
						color: style.COLORS.DEMOGRAPHIC.PRIMARY,
						boxShadow: hoveredFactor === 'AGREEABLENESS' ? '0px 0px 3px 3px rgba(0,0,0,0.25)' : 'none',
					}}
				>
					<Text textSize="22">AGREEABLENESS</Text>
				</Tag>
				<Tag
					style={{
						border: '2px solid',
						padding: '6px 8px',
						borderColor: style.COLORS.NEUROTICISM.PRIMARY,
						color: style.COLORS.DEMOGRAPHIC.PRIMARY,
						boxShadow: hoveredFactor === 'NEUROTICISM' ? '0px 0px 3px 3px rgba(0,0,0,0.25)' : 'none',
					}}
				>
					<Text textSize="22">NEUROTICISM</Text>
				</Tag>
				<Tag
					style={{
						border: '2px solid',
						padding: '6px 8px',
						borderColor: style.COLORS.DEMOGRAPHIC.PRIMARY,
						color: style.COLORS.DEMOGRAPHIC.PRIMARY,
						boxShadow: hoveredFactor === 'DEMOGRAPHIC' ? '0px 0px 3px 3px rgba(0,0,0,0.25)' : 'none',
					}}
				>
					<Text textSize="22">DEMOGRAPHIC</Text>
				</Tag>
			</Div>
		</>
	);
}

Heatmap.propTypes = {
	labels: traitsPropType.isRequired,
	series: PropTypes.arrayOf(
		PropTypes.shape({
			name: PropTypes.string,
			values: PropTypes.arrayOf(PropTypes.number),
			theme: PropTypes.string,
		})
	).isRequired,
	title: PropTypes.string,
	style: stylePropType,
	options: PropTypes.shape({}),
	formatter: PropTypes.func,
	onMouseover: PropTypes.func,
	strength: PropTypes.arrayOf(PropTypes.number),
	theme: PropTypes.string,
};

Heatmap.defaultProps = {
	title: '',
	style: Style1,
	options: {},
	formatter: (params) => params.value[2].toFixed(1),
	onMouseover: () => null,
	strength: [],
	theme: 'pinpoint',
};

export default Heatmap;
